import { useState, useEffect, useRef } from "react";
import { format } from "date-fns";
import { FieldValues, Path, PathValue, UseFormReturn } from "react-hook-form";

import { cn } from "@/lib/utils";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/Base/Form";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/Base/Popover";
import { ScrollArea, ScrollBar } from "@/components/Base/ScrollArea";
import { Button } from "@/components/Base/Button";
import { CalendarIcon } from "lucide-react";
import { Calendar } from "@/components/Base/Calendar";
import { Icon } from "@/components/Common/Icon";

type DateTimePickerFormProps<T extends FieldValues = FieldValues> = {
  form: UseFormReturn<T>;
  name: Path<T>;
  label?: string;
  description?: string;
  placeholder?: string;
  disabled?: boolean;
  defaultValue?: PathValue<T, Path<T>>;
  onChange?: (date: PathValue<T, Path<T>> | undefined) => void;
  canClear?: boolean;
  zIndex?: number;
  onOpen?: () => void;
  onClose?: () => void;
};

export const DateTimePickerForm = <T extends FieldValues = FieldValues>({
  form,
  name,
  label,
  defaultValue,
  placeholder,
  disabled,
  description,
  onChange,
  canClear = false,
  zIndex,
  onOpen,
  onClose,
}: DateTimePickerFormProps<T>) => {
  const [open, setOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Handle clicks outside the popover to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        open &&
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node)
      ) {
        onOpenChange(false);
      }
    };

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [open]);

  const handleDateSelect = (date: Date | undefined) => {
    if (date) {
      // shouldValidate is required, not cosmetic: a plain setValue does not
      // recompute formState.isValid, so a consumer that gates its submit
      // button on isValid (TaskForm) can never be submitted when the date is
      // the last field the user touches — which is the natural order.
      // shouldDirty is required for the same reason: a consumer that keys a
      // live-data re-reset off dirtyFields (CustomerUpdateForm, WEBV3-47)
      // would otherwise treat a picked date as untouched and silently
      // overwrite it back to a stale/default value.
      form.setValue(name, date as PathValue<T, Path<T>>, {
        shouldValidate: true,
        shouldDirty: true,
      });
      if (onChange) {
        onChange(date as PathValue<T, Path<T>>);
      }
    }
  };

  const handleTimeChange = (
    type: "hour" | "minute" | "ampm",
    value: string
  ) => {
    const currentDate = form.getValues(name) || new Date();
    const newDate = new Date(currentDate);

    if (type === "hour") {
      const hour = parseInt(value, 10);
      newDate.setHours(newDate.getHours() >= 12 ? hour + 12 : hour);
    } else if (type === "minute") {
      newDate.setMinutes(parseInt(value, 10));
    } else if (type === "ampm") {
      const hours = newDate.getHours();
      if (value === "AM" && hours >= 12) {
        newDate.setHours(hours - 12);
      } else if (value === "PM" && hours < 12) {
        newDate.setHours(hours + 12);
      }
    }

    // Same reason as handleDateSelect: without shouldValidate, choosing a
    // time leaves formState.isValid stale, and without shouldDirty it leaves
    // formState.dirtyFields stale.
    form.setValue(name, newDate as PathValue<T, Path<T>>, {
      shouldValidate: true,
      shouldDirty: true,
    });
    if (onChange) {
      onChange(newDate as PathValue<T, Path<T>>);
    }
  };

  const handleClear = () => {
    // Deliberately NOT validated, unlike the two handlers above — but only
    // because `canClear` defaults to false and no consumer passes it, so this
    // button is unreachable today. If you enable canClear, add
    // { shouldValidate: true } here as well: leaving it out would strand
    // formState.isValid at `true` over a cleared (null) value, which is worse
    // than the stuck-disabled bug this file's other handlers fix — it would
    // let a consumer submit a form it believes is valid.
    form.setValue(name, null as PathValue<T, Path<T>>);
    if (onChange) {
      onChange(undefined);
    }
  };

  const onOpenChange = (open: boolean) => {
    setOpen(open);
    if (open && onOpen) {
      onOpen();
    }
    if (!open && onClose) {
      onClose();
    }
  };

  return (
    <FormField
      control={form.control}
      name={name}
      defaultValue={defaultValue}
      disabled={disabled}
      render={({ field }) => (
        <FormItem className="flex flex-col">
          {label && <FormLabel>{label}</FormLabel>}
          <div className="flex flex-row justify-between gap-2">
            <Popover open={open} onOpenChange={onOpenChange}>
              <PopoverTrigger asChild>
                <FormControl>
                  <Button
                    disabled={disabled}
                    variant={"outline"}
                    className={cn(
                      "w-full pl-3 text-left font-normal",
                      !field.value && "text-muted-foreground"
                    )}
                  >
                    {field.value ? (
                      format(field.value, "MM/dd/yyyy hh:mm aa")
                    ) : (
                      <span>{placeholder ?? "MM / DD / YYYY hh:mm aa"}</span>
                    )}
                    <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                  </Button>
                </FormControl>
              </PopoverTrigger>
              <PopoverContent
                ref={popoverRef}
                className="w-auto p-0"
                style={{ zIndex: zIndex ?? 50 }} // Default zIndex was 50
              >
                <div className="sm:flex">
                  <Calendar
                    mode="single"
                    selected={field.value}
                    onSelect={handleDateSelect}
                  />
                  <div
                    className="flex flex-col divide-y sm:h-[300px] sm:flex-row sm:divide-x sm:divide-y-0"
                    onWheel={(e) => {
                      // Stop propagation to prevent parent components from capturing this event
                      e.stopPropagation();
                    }}
                  >
                    <ScrollArea className="w-64 sm:w-auto">
                      <div className="flex p-2 sm:flex-col">
                        {Array.from({ length: 12 }, (_, i) => i + 1)
                          .reverse()
                          .map((hour) => (
                            <Button
                              key={hour}
                              size="icon"
                              variant={
                                field.value &&
                                field.value.getHours() % 12 === hour % 12
                                  ? "default"
                                  : "ghost"
                              }
                              className="aspect-square shrink-0 sm:w-full"
                              onClick={() =>
                                handleTimeChange("hour", hour.toString())
                              }
                            >
                              {hour}
                            </Button>
                          ))}
                      </div>
                      <ScrollBar orientation="vertical" className="sm:hidden" />
                    </ScrollArea>
                    <ScrollArea className="w-64 sm:w-auto">
                      <div className="flex p-2 sm:flex-col">
                        {Array.from({ length: 12 }, (_, i) => i * 5).map(
                          (minute) => (
                            <Button
                              key={minute}
                              size="icon"
                              variant={
                                field.value &&
                                field.value.getMinutes() === minute
                                  ? "default"
                                  : "ghost"
                              }
                              className="aspect-square shrink-0 sm:w-full"
                              onClick={() =>
                                handleTimeChange("minute", minute.toString())
                              }
                            >
                              {minute.toString().padStart(2, "0")}
                            </Button>
                          )
                        )}
                      </div>
                      <ScrollBar orientation="vertical" className="sm:hidden" />
                    </ScrollArea>
                    <ScrollArea className="">
                      <div className="flex p-2 sm:flex-col">
                        {["AM", "PM"].map((ampm) => (
                          <Button
                            key={ampm}
                            size="icon"
                            variant={
                              field.value &&
                              ((ampm === "AM" && field.value.getHours() < 12) ||
                                (ampm === "PM" && field.value.getHours() >= 12))
                                ? "default"
                                : "ghost"
                            }
                            className="aspect-square shrink-0 sm:w-full"
                            onClick={() => handleTimeChange("ampm", ampm)}
                          >
                            {ampm}
                          </Button>
                        ))}
                      </div>
                    </ScrollArea>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
            {canClear && (
              <div className={"w-12"}>
                <Button
                  className={cn(field.value ? "visible" : "hidden")}
                  type={"button"}
                  size={"icon"}
                  variant={"secondary"}
                  onClick={handleClear}
                >
                  <Icon name={"clear"} />
                </Button>
              </div>
            )}
          </div>
          {description && <FormDescription>{description}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
};
